import { useMemo, useRef, useState } from 'react'
import { ARTICLES_PER_PAGE } from './constants'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import { Container, Pagination } from '@boltenergy-be/design-system'
import styles from './EncyclopediaArticles.module.scss'
import ArticleCard from 'components/article/article-card/ArticleCard'
import { EncyclopediaArticlesProps } from './types'

const EncyclopediaArticles = ({ articles, categories, defaultCategory = 'all' }: EncyclopediaArticlesProps) => {
  // Local state
  const [activePage, setActivePage] = useState<number>(1)

  // Refs
  const articlesRef = useRef<HTMLUListElement | null>(null)

  // i18n
  const { t } = useTranslation(['common', 'encyclopedia'])

  // Hook form
  const { control, register } = useForm<{ visibleCategory: string }>({
    defaultValues: {
      visibleCategory: defaultCategory
    }
  })
  const watchVisibleCategory = useWatch({ control, name: 'visibleCategory' })

  // Constants
  const filteredArticles = useMemo(() => {
    if (watchVisibleCategory === 'all') return articles
    return articles.filter((a) => a.category?.slug === watchVisibleCategory)
  }, [articles, watchVisibleCategory])
  const pagesLength = Math.ceil((filteredArticles?.length ?? 0) / ARTICLES_PER_PAGE)
  const articlesToShow = useMemo(
    () => filteredArticles?.slice((activePage - 1) * ARTICLES_PER_PAGE, activePage * ARTICLES_PER_PAGE) ?? [],
    [filteredArticles, activePage]
  )

  /**
   *
   * @param {number} page
   */
  const handlePageChange = (page: number) => {
    setActivePage(page)
    articlesRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <Container as="section">
      {categories && !!categories.length && (
        <form className={styles['filter-form']}>
          <ul className={styles.filters}>
            <li>
              <label htmlFor="all">
                <input
                  {...register('visibleCategory', {
                    onChange: () => {
                      setActivePage(1)
                    }
                  })}
                  type="radio"
                  value="all"
                  id="all"
                />
                {t('encyclopedia:allArticles')}
              </label>
            </li>

            {categories.map(({ slug, title }) => (
              <li key={slug}>
                <label htmlFor={slug}>
                  <input
                    {...register('visibleCategory', {
                      onChange: () => {
                        setActivePage(1)
                      }
                    })}
                    id={slug}
                    type="radio"
                    value={slug}
                  />
                  {title}
                </label>
              </li>
            ))}
          </ul>
        </form>
      )}

      <ul className={styles.articles} ref={articlesRef}>
        {articlesToShow.length ? (
          articlesToShow.map((article) => (
            <li key={`${article.title}-${article.id}`}>
              <ArticleCard {...article} publishedAt={article.publishedAt} destination={article.destination} className={styles.article} />
            </li>
          ))
        ) : (
          <li>{t('noArticlesFound')}</li>
        )}
      </ul>

      {pagesLength > 1 && (
        <Pagination pagesLength={pagesLength} activePageNumber={activePage} onPageChange={handlePageChange} className={styles.pagination} />
      )}
    </Container>
  )
}

export default EncyclopediaArticles
